/**
 * POS — the waiter's terminal.
 *
 * At most three screens, switched by one control, each filling the screen:
 * the tables, the orders, and the bookings. Only the ones this restaurant uses
 * are offered — a café with no tables has no tables screen — and the tablet
 * opens on whichever one it was set to (the floor tablet on the tables, the
 * counter tablet on the orders). It does not scroll as a page: on a tablet held
 * one-handed, a screen that moves under the thumb is a screen that mis-taps.
 *
 * Everything that is not service — which site, which plan, signing out — lives
 * behind the site button. Those are three taps a waiter should have to mean,
 * and a "Sign out" sitting permanently in the corner of a terminal is one
 * elbow away from ending someone's shift mid-order.
 */

import { useState } from 'react';
import {
  setDisplayFormat,
  useEntitlements,
  useFloor,
  useLocations,
  useMe,
  usePermission,
  usePractice,
  useSession,
} from '@restaurant/api-client';
import {
  Button,
  Empty,
  ErrorNotice,
  Loading,
  Segmented,
  Sheet,
  ThemePicker,
  UpgradePrompt,
} from '@restaurant/ui';

import { ClockSheet } from './clock';
import { useClockStatus, useDrawerStatus, useShiftSteps, type Status } from './shift-status';
import { PinSwap } from './pin';
import { LOCK_CHOICES, useLockMinutes } from './lock-store';
import { homeFor, useHomeChoice, type HomeChoice } from './home-store';
import { DrawerSheet, TillChoices } from './drawer';
import { FloorPlan } from './floor-plan';
import { Waitlist } from './waitlist';
import { Bookings } from './bookings';
import { CounterSaleButton, Orders } from './orders';
import { StartShiftButton, StartShiftSheet } from './shift-start';
import { OrderScreen } from './order-screen';

import './styles.css';
import { ReaderPicker } from './card-reader';
import { PrinterRow } from './printer-settings';
import { isTillApp } from './native';
import { TillAppRow } from './till-app';
import { PracticeRow } from './practice';
import { forgetPins } from './offline/pins';
import { OfflinePrep } from './offline/prep';

type View = 'tables' | 'orders' | 'bookings';

const VIEW_LABEL: Record<View, string> = {
  tables: 'Tables',
  orders: 'Orders',
  bookings: 'Bookings',
};

export function App() {
  const locations = useLocations();
  const entitlements = useEntitlements();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [openOrderId, setOpenOrderId] = useState<string | null>(null);
  const [picked, setPicked] = useState<View | null>(null);
  const [siteSheet, setSiteSheet] = useState(false);
  const [shiftSheet, setShiftSheet] = useState<'clock' | 'drawer' | 'start' | null>(null);
  const [homeChoice] = useHomeChoice();
  const mayManageFloor = usePermission('floor.plan.manage');
  const mayChangePlan = usePermission('billing.subscription.change');
  const practising = usePractice().practising;
  // Before the early returns: which screen to open on depends on whether the
  // restaurant has any tables.
  const floor = useFloor(activeId ?? locations.data?.[0]?.id);
  const clock = useClockStatus();
  const drawer = useDrawerStatus(activeId ?? locations.data?.[0]?.id);
  const shiftSteps = useShiftSteps(activeId ?? locations.data?.[0]?.id);

  if (locations.isPending) return <Loading label="Loading your restaurant" />;
  if (locations.isError) {
    return (
      <div className="pos__pad">
        <ErrorNotice message={locations.error.message} />
      </div>
    );
  }

  const active = locations.data.find((l) => l.id === activeId) ?? locations.data[0];
  // Every price on this terminal in this site's currency and format. Set
  // during render, before anything below formats a price.
  if (active) setDisplayFormat({ currency: active.currency, locale: active.locale });

  if (locations.data.length === 0) {
    return (
      <div className="pos__pad">
        <Empty
          title="No location set up yet"
          description="Add your restaurant in the back office, then reload this terminal."
        />
      </div>
    );
  }

  if (floor.isPending) return <Loading label="Loading your restaurant" />;

  const hasTables = (floor.data ?? []).some((plan) => plan.tables.length > 0);
  const takesBookings =
    entitlements.has('floor.reservations') || entitlements.has('floor.waitlist');

  // Only the screens this restaurant uses. No tables means no tables screen,
  // unless somebody here can set the room up; bookings appear for staff only
  // on a plan that has them, and as an offer only to whoever pays for it.
  const views: View[] = [
    ...(hasTables || mayManageFloor ? (['tables'] as const) : []),
    'orders',
    ...(takesBookings || mayChangePlan ? (['bookings'] as const) : []),
  ];
  const home = homeFor(homeChoice, hasTables);
  const view: View =
    picked && views.includes(picked) ? picked : views.includes(home) ? home : 'orders';

  // An open order takes the whole terminal. Nothing else on this screen is
  // relevant while someone is standing at a table reading an order back.
  if (openOrderId) {
    return (
      <div className="pos">
        <OrderScreen
          orderId={openOrderId}
          locationId={active?.id}
          backLabel={VIEW_LABEL[view]}
          onClose={() => setOpenOrderId(null)}
        />
      </div>
    );
  }

  return (
    <div className="pos">
      {/* Keeps what an offline till needs, while the line is up. Not while
          practising: practice does not sell offline, and its session may not
          take receipt numbers. */}
      {active && !practising && <OfflinePrep location={active} />}
      <header className="pos__bar">
        {/* The shift and the drawer, said in words under the site name rather
            than as two coloured pills: one place to look and one to tap. */}
        <button type="button" className="pos__site" onClick={() => setSiteSheet(true)}>
          <span className="pos__site-name">
            {active?.name ?? 'Restaurant'}
            <span className="pos__site-more" aria-hidden="true">
              ▾
            </span>
          </span>
          <StatusLine parts={[clock, drawer]} />
        </button>

        {/* Whatever is left before the first sale, as one errand. */}
        <StartShiftButton steps={shiftSteps} onOpen={() => setShiftSheet('start')} />

        <div className="pos__bar-right">
          <CounterSaleButton locationId={active?.id} onOpen={setOpenOrderId} />
          {views.length > 1 && (
            <Segmented
              label="Screen"
              value={view}
              onChange={setPicked}
              options={views.map((v) => ({ value: v, label: VIEW_LABEL[v] }))}
            />
          )}
        </div>
      </header>

      <main className="pos__main">
        {view === 'tables' ? (
          <FloorPlan locationId={active?.id} onOpenOrder={setOpenOrderId} />
        ) : view === 'orders' ? (
          <Orders
            locationId={active?.id}
            onOpen={setOpenOrderId}
            onPickTable={() => setPicked('tables')}
          />
        ) : (
          <BookingsView locationId={active?.id} onOpenOrder={setOpenOrderId} />
        )}
      </main>

      {siteSheet && (
        <SiteSheet
          clock={clock}
          drawer={drawer}
          onShift={(which) => {
            setSiteSheet(false);
            setShiftSheet(which);
          }}
          activeId={active?.id ?? null}
          onPick={(id) => {
            setActiveId(id);
            setSiteSheet(false);
          }}
          onDismiss={() => setSiteSheet(false)}
        />
      )}
      {shiftSheet === 'clock' && (
        <ClockSheet locationId={active?.id} onDismiss={() => setShiftSheet(null)} />
      )}
      {shiftSheet === 'drawer' && (
        <DrawerSheet locationId={active?.id} onDismiss={() => setShiftSheet(null)} />
      )}
      {shiftSheet === 'start' && (
        <StartShiftSheet locationId={active?.id} onDismiss={() => setShiftSheet(null)} />
      )}
    </div>
  );
}

/** "On shift 2h 05m · Drawer open", with anything that needs doing in its tone. */
function StatusLine({ parts }: { parts: (Status | null)[] }) {
  const said = parts.filter((p): p is Status => p !== null);
  if (said.length === 0) return null;
  return (
    <span className="pos__status">
      {said.map((part, i) => (
        <span key={part.label} className={`pos__status-part is-${part.tone}`}>
          {i > 0 && <span aria-hidden="true"> · </span>}
          {part.label}
        </span>
      ))}
    </span>
  );
}

/**
 * Bookings and the walk-in list on one screen, because they are one job: who is
 * coming, and where they will sit. Each is offered on the plan that has it.
 */
function BookingsView({
  locationId,
  onOpenOrder,
}: {
  locationId: string | undefined;
  onOpenOrder: (orderId: string) => void;
}) {
  const entitlements = useEntitlements();
  const booked = entitlements.has('floor.reservations');
  const walkIns = entitlements.has('floor.waitlist');
  const [part, setPart] = useState<'booked' | 'walk-ins'>(booked ? 'booked' : 'walk-ins');

  if (!booked && !walkIns) {
    return (
      <div className="pos__view">
        <div className="pos__view-body">
          <Empty
            title="Bookings are on Enterprise"
            description="Take bookings on the phone, keep a list of walk-ins at the door, and seat both straight onto a table."
          />
        </div>
      </div>
    );
  }

  return (
    <div className="pos__view">
      {booked && walkIns && (
        <div className="room__rooms">
          <Segmented
            label="Bookings or walk-ins"
            value={part}
            onChange={setPart}
            options={[
              { value: 'booked', label: 'Booked' },
              { value: 'walk-ins', label: 'Walk-ins' },
            ]}
          />
        </div>
      )}
      {booked && part === 'booked' ? (
        <Bookings locationId={locationId} onOpenOrder={onOpenOrder} />
      ) : (
        <Waitlist locationId={locationId} />
      )}
    </div>
  );
}

/**
 * Everything that is not taking an order.
 *
 * Behind a deliberate tap, because the two things in here — changing site and
 * signing out — are both ways to lose your place in the middle of service.
 */
function SiteSheet({
  clock,
  drawer,
  onShift,
  activeId,
  onPick,
  onDismiss,
}: {
  clock: Status | null;
  drawer: Status | null;
  onShift: (which: 'clock' | 'drawer') => void;
  activeId: string | null;
  onPick: (id: string) => void;
  onDismiss: () => void;
}) {
  const session = useSession();
  const me = useMe();
  const entitlements = useEntitlements();
  const locations = useLocations();
  // Offers to upgrade are for whoever pays, not for whoever is on shift.
  const mayChangePlan = usePermission('billing.subscription.change');

  return (
    <Sheet
      title="This tablet"
      onDismiss={onDismiss}
      actions={
        <Button
          variant="secondary"
          size="lg"
          block
          onClick={() => {
            // Whoever signs in next may be another restaurant's: no PIN of
            // this one's should open the lock for them offline.
            forgetPins();
            session.signOut();
          }}
        >
          Sign out
        </Button>
      }
    >
      <div className="pos__who-row">
        <div className="pos__who">
          <span className="pos__who-email">{me.data?.email ?? me.data?.name}</span>
          <span className="pos__who-plan">{entitlements.plan} plan</span>
        </div>
        {/* Swapping who is working the till, which is a different act from
            signing the till out — and the reason PINs exist. The terminal
            stays signed in; only the person changes. */}
        <PinSwap />
      </div>

      {/* First in the sheet: clocking in is the first thing somebody does on
          arriving, and the drawer the first thing before taking cash. */}
      {(clock || drawer) && (
        <ul className="os__more pos__shift-rows">
          {clock && (
            <li>
              <button type="button" className="os__more-item" onClick={() => onShift('clock')}>
                <span className="os__more-label">Your shift</span>
                <span className={`os__more-hint pos__status-part is-${clock.tone}`}>
                  {clock.label}
                </span>
              </button>
            </li>
          )}
          {drawer && (
            <li>
              <button type="button" className="os__more-item" onClick={() => onShift('drawer')}>
                <span className="os__more-label">Cash drawer</span>
                <span className={`os__more-hint pos__status-part is-${drawer.tone}`}>
                  {drawer.label}
                </span>
              </button>
            </li>
          )}
        </ul>
      )}

      {/* Near the top: it is how a new team learns the till, and down among
          the printer and reader settings nobody found it. */}
      <PracticeRow locationId={activeId ?? undefined} />

      <LockChoice />

      <HomeChoicePicker />

      {activeId && <ReaderPicker locationId={activeId} />}

      <PrinterRow />

      {isTillApp() && <TillAppRow />}

      {(locations.data ?? []).length > 1 && (
        <div className="pos__sites">
          {(locations.data ?? []).map((location) => (
            <button
              key={location.id}
              type="button"
              className={`pos__site-btn${location.id === activeId ? ' is-active' : ''}`}
              aria-pressed={location.id === activeId}
              onClick={() => onPick(location.id)}
            >
              <span className="pos__site-btn-name">{location.name}</span>
              <span className="pos__site-btn-code">{location.code}</span>
            </button>
          ))}
        </div>
      )}

      {/* Asked here as well as from the drawer, because it is a fact about
          this tablet rather than about a shift. Absent where no tills are
          registered: there is nothing to choose. */}
      <TillChoices locationId={activeId ?? undefined} label="This till" />

      {/* Also a fact about this tablet: the one bolted under the pass window
          wants dark whoever is signed in. */}
      <ThemePicker />

      {mayChangePlan && !entitlements.has('tenancy.multi_location') && (
        <UpgradePrompt
          action="A second location"
          cap={1}
          currentPlan={entitlements.plan}
          requiredPlan="professional"
          compact
        />
      )}

      {mayChangePlan && !entitlements.has('orders.offline') && (
        <UpgradePrompt
          action="Offline ordering"
          currentPlan={entitlements.plan}
          requiredPlan="professional"
          compact
        />
      )}
    </Sheet>
  );
}

/** How long this tablet waits before asking for a PIN again. */
function LockChoice() {
  const [minutes, setMinutes] = useLockMinutes();
  return (
    <div className="pos__till">
      <span className="pos__till-label">Lock after</span>
      <Segmented
        label="Lock after"
        value={String(minutes)}
        onChange={(next) => setMinutes(Number(next) as (typeof LOCK_CHOICES)[number])}
        options={LOCK_CHOICES.map((m) => ({
          value: String(m),
          label: m === 0 ? 'Never' : `${m} min`,
        }))}
      />
    </div>
  );
}

/** Which screen this tablet opens on. */
function HomeChoicePicker() {
  const [choice, setChoice] = useHomeChoice();
  return (
    <div className="pos__till">
      <span className="pos__till-label">Opens on</span>
      <Segmented
        label="Opens on"
        value={choice}
        onChange={(next) => setChoice(next as HomeChoice)}
        options={[
          { value: 'auto', label: 'Tables if any' },
          { value: 'tables', label: 'Tables' },
          { value: 'orders', label: 'Orders' },
        ]}
      />
    </div>
  );
}
